import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Plus, Download, Trash2, ClipboardList, Loader2, Search, X,
  Camera, ImageIcon, ChevronDown, ChevronUp, Eye, PenLine, CheckCircle2, Pencil,
} from "lucide-react";
import { getEdls, getTenants, createEdl, updateEdl, deleteEdl, downloadEdlPdf } from "@/lib/api";
import { toast } from "sonner";

// ── Composant pad de signature tactile ────────────────────────────────────────
function SignaturePad({ label, value, onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return {
      x: (touch.clientX - rect.left) * (canvas.width / rect.width),
      y: (touch.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawing.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1a1a2e";
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDraw = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const canvas = canvasRef.current;
    // Vérifier si quelque chose a été dessiné
    const ctx = canvas.getContext("2d");
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    const hasContent = data.some(v => v !== 0);
    onChange(hasContent ? canvas.toDataURL("image/png") : "");
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onChange("");
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold text-gray-700">{label}</Label>
        <button type="button" onClick={clear} className="text-xs text-gray-400 hover:text-red-500 transition-colors">
          Effacer
        </button>
      </div>
      <div className={`border-2 rounded-xl bg-white relative overflow-hidden transition-colors ${value ? "border-green-400" : "border-dashed border-gray-300"}`}>
        <canvas
          ref={canvasRef}
          width={500}
          height={160}
          className="w-full touch-none cursor-crosshair"
          style={{ display: "block" }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={stopDraw}
          onMouseLeave={stopDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={stopDraw}
        />
        {!value && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-gray-300 text-sm select-none">Signez ici</span>
          </div>
        )}
      </div>
      {value && (
        <p className="text-xs text-green-600 flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" /> Signature enregistrée
        </p>
      )}
    </div>
  );
}

const STRUCTURES = [
  { label: "Centre Médical Madeleinois — La Madeleine", adresse: "184 b avenue du Général de Gaulle 59110 La Madeleine" },
  { label: "Seclin",       adresse: "15 rue Maurice Bouchery 59113 Seclin" },
  { label: "Armentieres",  adresse: "1 rue de Strasbourg 59155 Armentieres" },
];

const PIECES_DEFAULT = [
  { nom: "Salle d'attente", elements: [], photos: [], observations: "" },
  { nom: "Bureau de consultation", elements: [], photos: [], observations: "" },
  { nom: "Salle de soins", elements: [], photos: [], observations: "" },
  { nom: "Salle de réveil", elements: [], photos: [], observations: "" },
  { nom: "Toilettes", elements: [], photos: [], observations: "" },
  { nom: "Couloir", elements: [], photos: [], observations: "" },
];

const ETATS = ["bon", "moyen", "mauvais", "non vérifié"];

const ETAT_COLORS = {
  "bon": "bg-green-100 text-green-700 border-green-200",
  "moyen": "bg-yellow-100 text-yellow-700 border-yellow-200",
  "mauvais": "bg-red-100 text-red-700 border-red-200",
  "non vérifié": "bg-gray-100 text-gray-600 border-gray-200",
};

const ETAT_DOT = {
  "bon": "bg-green-500",
  "moyen": "bg-yellow-500",
  "mauvais": "bg-red-500",
  "non vérifié": "bg-gray-400",
};

export default function EtatsDesLieux() {
  const [edls, setEdls] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editEdlId, setEditEdlId] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [signDialog, setSignDialog] = useState({ open: false, edl: null, signed: false });
  const [signLoading, setSignLoading] = useState(false);
  const [signSigs, setSignSigs] = useState({ bailleur: "", locataire: "" });
  const [pieces, setPieces] = useState(PIECES_DEFAULT);
  const [newPiece, setNewPiece] = useState("");
  const [expandedPieces, setExpandedPieces] = useState({});
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [formData, setFormData] = useState({
    tenant_id: "",
    structure_preset: "",
    type: "entrée",
    date: new Date().toISOString().split("T")[0],
    locataire_nom: "",
    local_adresse: "",
    bailleur_nom: "",
    bailleur_present: true,
    locataire_present: true,
    releve_eau: "",
    releve_electricite: "",
    releve_gaz: "",
    nb_cles: "",
    nb_badges: "",
    observations_generales: "",
    signature_bailleur: "",
    signature_locataire: "",
  });

  const fileInputRefs = useRef({});

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [eRes, tRes] = await Promise.all([getEdls(), getTenants()]);
      setEdls(eRes.data);
      setTenants(tRes.data);
    } catch {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Auto-expand all pieces when dialog opens
  useEffect(() => {
    if (dialogOpen) {
      const expanded = {};
      pieces.forEach((_, i) => { expanded[i] = true; });
      setExpandedPieces(expanded);
    }
  }, [dialogOpen]);

  const handleTenantChange = (tenantId) => {
    const t = tenants.find(t => t.id === tenantId || t._id === tenantId);
    if (!t) return setFormData(f => ({ ...f, tenant_id: tenantId }));
    setFormData(f => ({
      ...f,
      tenant_id: tenantId,
      locataire_nom: t.name || f.locataire_nom,
      local_adresse: t.property_address || f.local_adresse,
    }));
  };

  const togglePiece = (idx) => {
    setExpandedPieces(ep => ({ ...ep, [idx]: !ep[idx] }));
  };

  // Photo upload — convert files to base64
  const handlePhotoUpload = (pieceIdx, files) => {
    Array.from(files).forEach(file => {
      if (!file.type.startsWith("image/")) return;
      // Resize before base64 to keep payload manageable
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const MAX = 1200;
          let w = img.width, h = img.height;
          if (w > MAX || h > MAX) {
            if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
            else { w = Math.round(w * MAX / h); h = MAX; }
          }
          canvas.width = w;
          canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          const b64 = canvas.toDataURL("image/jpeg", 0.82);
          setPieces(ps => ps.map((p, i) =>
            i === pieceIdx ? { ...p, photos: [...p.photos, b64] } : p
          ));
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (pieceIdx, photoIdx) => {
    setPieces(ps => ps.map((p, i) =>
      i === pieceIdx ? { ...p, photos: p.photos.filter((_, j) => j !== photoIdx) } : p
    ));
  };

  const updatePieceObservation = (idx, val) => {
    setPieces(ps => ps.map((p, i) => i === idx ? { ...p, observations: val } : p));
  };

  const addElementToPiece = (pieceIdx, element) => {
    setPieces(ps => ps.map((p, i) =>
      i === pieceIdx ? { ...p, elements: [...p.elements, { nom: element, etat: "non vérifié", observations: "" }] } : p
    ));
  };

  const updateElement = (pieceIdx, elIdx, field, val) => {
    setPieces(ps => ps.map((p, i) =>
      i === pieceIdx
        ? { ...p, elements: p.elements.map((el, j) => j === elIdx ? { ...el, [field]: val } : el) }
        : p
    ));
  };

  const removeElement = (pieceIdx, elIdx) => {
    setPieces(ps => ps.map((p, i) =>
      i === pieceIdx ? { ...p, elements: p.elements.filter((_, j) => j !== elIdx) } : p
    ));
  };

  const addPiece = () => {
    if (!newPiece.trim()) return;
    const idx = pieces.length;
    setPieces(ps => [...ps, { nom: newPiece.trim(), elements: [], photos: [], observations: "" }]);
    setExpandedPieces(ep => ({ ...ep, [idx]: true }));
    setNewPiece("");
  };

  const removePiece = (idx) => setPieces(ps => ps.filter((_, i) => i !== idx));

  const resetForm = () => {
    setPieces(PIECES_DEFAULT);
    setFormData({
      tenant_id: "", structure_preset: "", type: "entrée",
      date: new Date().toISOString().split("T")[0],
      locataire_nom: "", local_adresse: "", bailleur_nom: "",
      bailleur_present: true, locataire_present: true,
      releve_eau: "", releve_electricite: "", releve_gaz: "",
      nb_cles: "", nb_badges: "", observations_generales: "",
      signature_bailleur: "", signature_locataire: "",
    });
    setEditMode(false);
    setEditEdlId(null);
  };

  const openEditDialog = (edl) => {
    setEditMode(true);
    setEditEdlId(edl.id);
    setFormData({
      tenant_id: edl.tenant_id || "",
      structure_preset: edl.structure_preset || "",
      type: edl.type || "entrée",
      date: edl.date || new Date().toISOString().split("T")[0],
      locataire_nom: edl.locataire_nom || "",
      local_adresse: edl.local_adresse || "",
      bailleur_nom: edl.bailleur_nom || "",
      bailleur_present: edl.bailleur_present ?? true,
      locataire_present: edl.locataire_present ?? true,
      releve_eau: edl.releve_eau != null ? String(edl.releve_eau) : "",
      releve_electricite: edl.releve_electricite != null ? String(edl.releve_electricite) : "",
      releve_gaz: edl.releve_gaz != null ? String(edl.releve_gaz) : "",
      nb_cles: edl.nb_cles != null ? String(edl.nb_cles) : "",
      nb_badges: edl.nb_badges != null ? String(edl.nb_badges) : "",
      observations_generales: edl.observations_generales || "",
      signature_bailleur: edl.signature_bailleur || "",
      signature_locataire: edl.signature_locataire || "",
    });
    setPieces(edl.pieces?.length ? edl.pieces : PIECES_DEFAULT);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = {
        ...formData,
        nb_cles: formData.nb_cles ? parseInt(formData.nb_cles) : null,
        nb_badges: formData.nb_badges ? parseInt(formData.nb_badges) : null,
        pieces,
      };
      if (editMode) {
        await updateEdl(editEdlId, payload);
        toast.success("État des lieux mis à jour avec succès");
      } else {
        await createEdl(payload);
        toast.success("État des lieux créé avec succès");
      }
      setDialogOpen(false);
      resetForm();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDownload = async (edl) => {
    setDownloadingId(edl.id);
    try {
      const res = await downloadEdlPdf(edl.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `EDL_${edl.type}_${edl.locataire_nom}_${edl.date}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur lors du téléchargement");
    } finally {
      setDownloadingId(null);
    }
  };

  const openSignDialog = (edl) => {
    setSignSigs({
      bailleur: edl.signature_bailleur || "",
      locataire: edl.signature_locataire || "",
    });
    setSignDialog({ open: true, edl });
  };

  const handleSign = async () => {
    if (!signSigs.bailleur && !signSigs.locataire) {
      toast.error("Veuillez ajouter au moins une signature");
      return;
    }
    setSignLoading(true);
    try {
      await updateEdl(signDialog.edl.id, {
        signature_bailleur: signSigs.bailleur || null,
        signature_locataire: signSigs.locataire || null,
      });
      toast.success("Signatures enregistrées");
      // On reste dans le dialog pour proposer le téléchargement immédiat
      setSignDialog(s => ({ ...s, signed: true }));
      fetchAll();
    } catch {
      toast.error("Erreur lors de l'enregistrement des signatures");
    } finally {
      setSignLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cet état des lieux ?")) return;
    try {
      await deleteEdl(id);
      toast.success("État des lieux supprimé");
      fetchAll();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const filtered = edls.filter(e =>
    e.locataire_nom?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.local_adresse?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const set = (field) => (e) => setFormData(f => ({ ...f, [field]: e.target ? e.target.value : e }));

  const totalPhotos = pieces.reduce((acc, p) => acc + (p.photos?.length || 0), 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">États des lieux</h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{edls.length} enregistré(s)</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button size="sm" className="sm:size-default">
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Nouvel EDL</span>
            </Button>
          </DialogTrigger>

          {/* ── Formulaire EDL ── */}
          <DialogContent className="w-full max-w-full sm:max-w-2xl h-[100dvh] sm:h-auto sm:max-h-[92vh] overflow-y-auto p-0 sm:p-6 rounded-none sm:rounded-lg">
            <div className="sticky top-0 bg-white border-b sm:border-none px-4 py-3 sm:p-0 sm:mb-0 z-10">
              <DialogHeader>
                <DialogTitle className="text-base sm:text-lg">{editMode ? "Modifier l'état des lieux" : "Nouvel état des lieux"}</DialogTitle>
              </DialogHeader>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 px-4 pb-6 sm:px-0 sm:pb-0 mt-4 sm:mt-0">

              {/* ── Section : Infos générales ── */}
              <section className="space-y-3">
                <h3 className="font-semibold text-xs text-gray-500 uppercase tracking-wider">Informations générales</h3>

                {/* Structure — menu déroulant pré-remplit l'adresse */}
                <div>
                  <Label className="text-sm">Structure</Label>
                  <Select
                    value={formData.structure_preset}
                    onValueChange={val => {
                      const s = STRUCTURES.find(s => s.label === val);
                      setFormData(f => ({
                        ...f,
                        structure_preset: val,
                        local_adresse: s ? s.adresse : f.local_adresse,
                      }));
                    }}
                  >
                    <SelectTrigger className="h-11 sm:h-9 text-sm">
                      <SelectValue placeholder="Sélectionner une structure..." />
                    </SelectTrigger>
                    <SelectContent>
                      {STRUCTURES.map(s => (
                        <SelectItem key={s.label} value={s.label} className="text-sm">{s.label}</SelectItem>
                      ))}
                      <SelectItem value="autre" className="text-sm text-gray-400">Autre (saisie libre)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Locataire — selector depuis la BDD */}
                <div>
                  <Label className="text-sm">Locataire (depuis la base)</Label>
                  <Select onValueChange={handleTenantChange} value={formData.tenant_id}>
                    <SelectTrigger className="h-11 sm:h-9 text-sm">
                      <SelectValue placeholder="Sélectionner un locataire..." />
                    </SelectTrigger>
                    <SelectContent>
                      {tenants.map(t => (
                        <SelectItem key={t.id || t._id} value={t.id || t._id} className="text-sm">
                          {t.name}
                          {t.property_address && <span className="text-gray-400 ml-2 text-xs">— {t.property_address}</span>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Nom locataire *</Label>
                    <Input className="h-11 sm:h-9 text-sm" value={formData.locataire_nom} onChange={set("locataire_nom")} required />
                  </div>
                  <div>
                    <Label className="text-sm">Adresse du local *</Label>
                    <Input className="h-11 sm:h-9 text-sm" value={formData.local_adresse} onChange={set("local_adresse")} required />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-sm">Type</Label>
                    <Select onValueChange={set("type")} value={formData.type}>
                      <SelectTrigger className="h-11 sm:h-9 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="entrée">Entrée</SelectItem>
                        <SelectItem value="sortie">Sortie</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Date *</Label>
                    <Input className="h-11 sm:h-9 text-sm" type="date" value={formData.date} onChange={set("date")} required />
                  </div>
                  <div>
                    <Label className="text-sm">Nom bailleur</Label>
                    <Input className="h-11 sm:h-9 text-sm" value={formData.bailleur_nom} onChange={set("bailleur_nom")} />
                  </div>
                </div>

                <div className="flex flex-wrap gap-4 pt-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Switch checked={formData.bailleur_present} onCheckedChange={v => setFormData(f => ({ ...f, bailleur_present: v }))} />
                    <span className="text-sm text-gray-700">Bailleur présent</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Switch checked={formData.locataire_present} onCheckedChange={v => setFormData(f => ({ ...f, locataire_present: v }))} />
                    <span className="text-sm text-gray-700">Locataire présent</span>
                  </label>
                </div>
              </section>

              {/* ── Section : Relevés ── */}
              <section className="space-y-3">
                <h3 className="font-semibold text-xs text-gray-500 uppercase tracking-wider">Relevés & Clés</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Eau (m³)</Label>
                    <Input className="h-11 sm:h-9 text-sm" value={formData.releve_eau} onChange={set("releve_eau")} placeholder="0" />
                  </div>
                  <div>
                    <Label className="text-xs">Électricité (kWh)</Label>
                    <Input className="h-11 sm:h-9 text-sm" value={formData.releve_electricite} onChange={set("releve_electricite")} placeholder="0" />
                  </div>
                  <div>
                    <Label className="text-xs">Gaz (m³)</Label>
                    <Input className="h-11 sm:h-9 text-sm" value={formData.releve_gaz} onChange={set("releve_gaz")} placeholder="0" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Nb clés</Label>
                    <Input className="h-11 sm:h-9 text-sm" type="number" value={formData.nb_cles} onChange={set("nb_cles")} />
                  </div>
                  <div>
                    <Label className="text-xs">Nb badges</Label>
                    <Input className="h-11 sm:h-9 text-sm" type="number" value={formData.nb_badges} onChange={set("nb_badges")} />
                  </div>
                </div>
              </section>

              {/* ── Section : Pièces ── */}
              <section className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-xs text-gray-500 uppercase tracking-wider">
                    État des pièces
                    {totalPhotos > 0 && (
                      <span className="ml-2 bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded-full">
                        {totalPhotos} photo{totalPhotos > 1 ? "s" : ""}
                      </span>
                    )}
                  </h3>
                </div>

                {pieces.map((piece, pIdx) => (
                  <div key={pIdx} className="border border-gray-200 rounded-xl overflow-hidden">
                    {/* En-tête de pièce */}
                    <button
                      type="button"
                      onClick={() => togglePiece(pIdx)}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-gray-800">{piece.nom}</span>
                        <div className="flex gap-1 items-center">
                          {piece.elements.length > 0 && (
                            <span className="text-xs text-gray-400">{piece.elements.length} élément{piece.elements.length > 1 ? "s" : ""}</span>
                          )}
                          {piece.photos.length > 0 && (
                            <span className="flex items-center gap-0.5 text-xs text-blue-500">
                              <ImageIcon className="w-3 h-3" />{piece.photos.length}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); removePiece(pIdx); }}
                          className="p-1 text-gray-400 hover:text-red-500 rounded"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                        {expandedPieces[pIdx] ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </button>

                    {expandedPieces[pIdx] && (
                      <div className="p-3 space-y-3 bg-white">
                        {/* Éléments */}
                        {piece.elements.map((el, elIdx) => (
                          <div key={elIdx} className="flex flex-col sm:flex-row gap-2 p-2 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${ETAT_DOT[el.etat] || "bg-gray-400"}`} />
                              <span className="text-sm font-medium text-gray-700 truncate">{el.nom}</span>
                            </div>
                            <div className="flex gap-2 items-center">
                              <Select value={el.etat} onValueChange={v => updateElement(pIdx, elIdx, "etat", v)}>
                                <SelectTrigger className="h-8 text-xs w-32 flex-shrink-0">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {ETATS.map(e => (
                                    <SelectItem key={e} value={e}>
                                      <div className="flex items-center gap-1.5">
                                        <div className={`w-2 h-2 rounded-full ${ETAT_DOT[e]}`} />
                                        {e}
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Input
                                className="h-8 text-xs flex-1"
                                placeholder="Observations..."
                                value={el.observations}
                                onChange={e => updateElement(pIdx, elIdx, "observations", e.target.value)}
                              />
                              <button type="button" onClick={() => removeElement(pIdx, elIdx)} className="text-gray-400 hover:text-red-500 flex-shrink-0">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}

                        {/* Ajouter élément */}
                        <div className="flex gap-2">
                          <Input
                            id={`new-el-${pIdx}`}
                            className="h-10 text-sm bg-gray-50 flex-1"
                            placeholder="Ajouter un élément..."
                            onKeyDown={e => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                if (e.target.value.trim()) {
                                  addElementToPiece(pIdx, e.target.value.trim());
                                  e.target.value = "";
                                }
                              }
                            }}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-10 px-3 flex-shrink-0"
                            onClick={() => {
                              const input = document.getElementById(`new-el-${pIdx}`);
                              if (input?.value.trim()) {
                                addElementToPiece(pIdx, input.value.trim());
                                input.value = "";
                              }
                            }}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>

                        {/* Observations pièce */}
                        <Textarea
                          className="text-sm resize-none"
                          placeholder="Observations de la pièce..."
                          rows={2}
                          value={piece.observations}
                          onChange={e => updatePieceObservation(pIdx, e.target.value)}
                        />

                        {/* ── Photos ── */}
                        <div className="space-y-2">
                          <input
                            ref={el => fileInputRefs.current[pIdx] = el}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            multiple
                            className="hidden"
                            onChange={e => { handlePhotoUpload(pIdx, e.target.files); e.target.value = ""; }}
                          />
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="flex-1 h-10 border-dashed text-sm text-gray-500 hover:text-gray-700 hover:border-gray-400"
                              onClick={() => fileInputRefs.current[pIdx]?.click()}
                            >
                              <Camera className="w-4 h-4 mr-2" />
                              Prendre / ajouter photo
                            </Button>
                          </div>

                          {piece.photos.length > 0 && (
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                              {piece.photos.map((photo, photoIdx) => (
                                <div key={photoIdx} className="relative group aspect-square">
                                  <img
                                    src={photo}
                                    alt={`Photo ${photoIdx + 1}`}
                                    className="w-full h-full object-cover rounded-lg border border-gray-200 cursor-pointer"
                                    onClick={() => setPreviewPhoto(photo)}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removePhoto(pIdx, photoIdx)}
                                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-100 transition-opacity shadow"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setPreviewPhoto(photo)}
                                    className="absolute bottom-1 right-1 bg-black/40 text-white rounded p-0.5 opacity-100 transition-opacity"
                                  >
                                    <Eye className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Ajouter pièce */}
                <div className="flex gap-2 pt-1">
                  <Input
                    value={newPiece}
                    onChange={e => setNewPiece(e.target.value)}
                    placeholder="Nom de la nouvelle pièce..."
                    className="text-sm h-10"
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addPiece(); } }}
                  />
                  <Button type="button" variant="outline" size="sm" onClick={addPiece} className="h-10 px-3">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </section>

              {/* ── Observations générales ── */}
              <section>
                <Label className="text-sm">Observations générales</Label>
                <Textarea
                  className="mt-1 text-sm resize-none"
                  value={formData.observations_generales}
                  onChange={set("observations_generales")}
                  rows={3}
                  placeholder="Remarques globales sur l'état du bien..."
                />
              </section>

              {/* ── Signatures ── */}
              <section className="space-y-4">
                <h3 className="font-semibold text-xs text-gray-500 uppercase tracking-wider flex items-center gap-2">
                  <PenLine className="w-3.5 h-3.5" />
                  Signatures (optionnel)
                </h3>
                <SignaturePad
                  label="Signature du bailleur"
                  value={formData.signature_bailleur}
                  onChange={v => setFormData(f => ({ ...f, signature_bailleur: v }))}
                />
                <SignaturePad
                  label="Signature du locataire"
                  value={formData.signature_locataire}
                  onChange={v => setFormData(f => ({ ...f, signature_locataire: v }))}
                />
              </section>

              {/* ── Submit ── */}
              <div className="flex gap-2 pt-2 sticky bottom-0 bg-white pb-2 sm:static sm:pb-0">
                <Button type="button" variant="outline" className="flex-1 sm:flex-none" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={formLoading} className="flex-1">
                  {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editMode ? "Mettre à jour l'EDL" : "Enregistrer l'EDL"}
                  {totalPhotos > 0 && <span className="ml-1 text-xs opacity-75">({totalPhotos} photo{totalPhotos > 1 ? "s" : ""})</span>}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* ── Aperçu photo plein écran ── */}
      {previewPhoto && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewPhoto(null)}
        >
          <button className="absolute top-4 right-4 text-white bg-black/40 rounded-full p-2" onClick={() => setPreviewPhoto(null)}>
            <X className="w-6 h-6" />
          </button>
          <img src={previewPhoto} alt="Aperçu" className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}

      {/* ── Recherche ── */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input className="pl-9 h-10" placeholder="Rechercher un locataire, une adresse..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
      </div>

      {/* ── Liste EDL ── */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">Aucun état des lieux</p>
          <p className="text-sm mt-1">Commencez par créer un EDL avec le bouton +</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(edl => (
            <Card key={edl.id} className="overflow-hidden hover:shadow-md transition-shadow">
              <CardContent className="p-0">
                <div className="flex items-stretch">
                  {/* Bande colorée */}
                  <div className={`w-1.5 flex-shrink-0 ${edl.type === "entrée" ? "bg-blue-500" : "bg-orange-500"}`} />
                  <div className="flex-1 p-3 sm:p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`text-xs ${edl.type === "entrée" ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"}`}>
                            {edl.type}
                          </Badge>
                          <span className="font-semibold text-gray-900 text-sm">{edl.locataire_nom}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">{edl.local_adresse}</p>
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
                          <span>{edl.date}</span>
                          {edl.pieces?.length > 0 && <span>{edl.pieces.length} pièce{edl.pieces.length > 1 ? "s" : ""}</span>}
                          {edl.pieces?.some(p => p.photos?.length > 0) && (
                            <span className="flex items-center gap-0.5 text-blue-500">
                              <ImageIcon className="w-3 h-3" />
                              {edl.pieces.reduce((acc, p) => acc + (p.photos?.length || 0), 0)} photo{edl.pieces.reduce((acc, p) => acc + (p.photos?.length || 0), 0) > 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0 items-center">
                        {(edl.signature_bailleur || edl.signature_locataire) && (
                          <span title="Signé" className="text-green-500">
                            <CheckCircle2 className="w-4 h-4" />
                          </span>
                        )}
                        <Button
                          size="sm" variant="outline"
                          className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                          title="Modifier"
                          onClick={() => openEditDialog(edl)}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          className="h-8 w-8 p-0 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50"
                          title="Faire signer"
                          onClick={() => openSignDialog(edl)}
                        >
                          <PenLine className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => handleDownload(edl)} disabled={downloadingId === edl.id}>
                          {downloadingId === edl.id
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <Download className="w-3.5 h-3.5" />}
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleDelete(edl.id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── Dialog signature EDL existant ── */}
      <Dialog open={signDialog.open} onOpenChange={open => {
        setSignDialog(s => ({ ...s, open }));
        if (!open) setSignDialog({ open: false, edl: null, signed: false });
      }}>
        <DialogContent className="w-full max-w-full sm:max-w-lg h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-y-auto p-0 sm:p-6 rounded-none sm:rounded-lg">
          <div className="sticky top-0 bg-white border-b sm:border-none px-4 py-3 sm:p-0 z-10">
            <DialogHeader>
              <DialogTitle className="text-base flex items-center gap-2">
                {signDialog.signed
                  ? <CheckCircle2 className="w-4 h-4 text-green-500" />
                  : <PenLine className="w-4 h-4 text-indigo-500" />}
                {signDialog.signed ? "Signatures enregistrées" : `Signatures — ${signDialog.edl?.locataire_nom}`}
              </DialogTitle>
            </DialogHeader>
          </div>

          <div className="px-4 pb-6 sm:px-0 sm:pb-0 mt-4 sm:mt-0">
            {signDialog.signed ? (
              /* ── État post-signature : proposer le téléchargement ── */
              <div className="space-y-5">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center space-y-2">
                  <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto" />
                  <p className="font-semibold text-green-800 text-sm">Document signé avec succès</p>
                  <p className="text-xs text-green-600">Les signatures ont été enregistrées. Téléchargez le PDF pour obtenir le document final.</p>
                </div>
                <div className="flex gap-2 sticky bottom-0 bg-white pb-2 sm:static sm:pb-0">
                  <Button
                    variant="outline"
                    className="flex-1 sm:flex-none"
                    onClick={() => setSignDialog({ open: false, edl: null, signed: false })}
                  >
                    Fermer
                  </Button>
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      handleDownload(signDialog.edl);
                      setSignDialog({ open: false, edl: null, signed: false });
                    }}
                    disabled={downloadingId === signDialog.edl?.id}
                  >
                    {downloadingId === signDialog.edl?.id
                      ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : <Download className="w-4 h-4 mr-2" />}
                    Télécharger le PDF signé
                  </Button>
                </div>
              </div>
            ) : (
              /* ── État signature ── */
              <div className="space-y-5">
                <p className="text-xs text-gray-500">
                  Faites signer directement sur l'écran. Les signatures seront intégrées au PDF.
                </p>
                <SignaturePad
                  label="Signature du bailleur"
                  value={signSigs.bailleur}
                  onChange={v => setSignSigs(s => ({ ...s, bailleur: v }))}
                />
                <SignaturePad
                  label="Signature du locataire"
                  value={signSigs.locataire}
                  onChange={v => setSignSigs(s => ({ ...s, locataire: v }))}
                />
                <div className="flex gap-2 sticky bottom-0 bg-white pb-2 sm:static sm:pb-0">
                  <Button variant="outline" className="flex-1 sm:flex-none"
                    onClick={() => setSignDialog({ open: false, edl: null, signed: false })}>
                    Annuler
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={handleSign}
                    disabled={signLoading || (!signSigs.bailleur && !signSigs.locataire)}
                  >
                    {signLoading
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enregistrement...</>
                      : <><CheckCircle2 className="w-4 h-4 mr-2" />Valider les signatures</>}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
